import { useState } from 'react';
import Navbar from '../shop/components/Navbar';
import FilterSidebar from './components/FilterSidebar';
import ProductGrid from './components/ProductGrid';
import Footer from '../home/components/Footer';

export default function ApparelPage() {
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('all');
  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
  const [selectedColors, setSelectedColors] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 500]);
  const [sortBy, setSortBy] = useState<string>('featured');

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      
      {/* Category Hero Banner */}
      <div className="relative h-[400px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/ccce17bc1427c5add2eebf25eedcf77c.jpg"
            alt="Apparel Collection"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/80"></div>
        </div>
        
        <div className="relative h-full max-w-[1600px] mx-auto px-8 flex flex-col justify-center">
          <div className="max-w-2xl">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-gray-300 mb-4">
              CATEGORY
            </p>
            <h1 className="text-7xl md:text-8xl font-black text-white mb-6">
              APPAREL
            </h1>
            <p className="text-lg text-gray-300 font-medium leading-relaxed">
              Statement pieces built for the culture. From varsity jackets to everyday essentials, 
              every piece represents quality, style, and authenticity.
            </p>
          </div>
        </div>
      </div>

      {/* Subcategory Quick Nav */}
      <div className="border-b border-white/10 bg-[#0A0A0A] sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-8 py-4">
          <div className="flex items-center gap-3 overflow-x-auto scrollbar-hide">
            {['all', 'jackets', 'hoodies', 'tees', 'pants', 'outerwear'].map((sub) => (
              <button
                key={sub}
                onClick={() => setSelectedSubcategory(sub)}
                className={`px-6 py-2 rounded-full text-sm font-bold tracking-wide transition-all whitespace-nowrap ${
                  selectedSubcategory === sub
                    ? 'bg-white text-black'
                    : 'bg-[#1A1A1A] text-gray-400 hover:text-white hover:bg-white/10'
                }`}
              >
                {sub === 'all' ? 'ALL APPAREL' : sub.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Controls Bar */}
      <div className="border-b border-white/10">
        <div className="max-w-[1600px] mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setIsMobileFilterOpen(true)}
                className="lg:hidden bg-[#1A1A1A] text-white border border-white/20 rounded-lg px-4 py-2 text-sm font-bold hover:border-white/40 transition-colors whitespace-nowrap"
              >
                <i className="ri-filter-3-line mr-2"></i>
                FILTERS
              </button>
            </div>
            
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-400 hidden md:block">Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-[#1A1A1A] text-white border border-white/20 rounded-lg px-4 py-2 text-sm font-medium cursor-pointer hover:border-white/40 transition-colors"
              >
                <option value="featured">Featured</option>
                <option value="newest">Newest</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1600px] mx-auto px-8 py-12">
        <div className="flex gap-8">
          {/* Desktop Filter Sidebar */}
          <div className="hidden lg:block w-64 flex-shrink-0">
            <FilterSidebar
              category="apparel"
              selectedSubcategory={selectedSubcategory}
              setSelectedSubcategory={setSelectedSubcategory}
              selectedSizes={selectedSizes}
              setSelectedSizes={setSelectedSizes}
              selectedColors={selectedColors}
              setSelectedColors={setSelectedColors}
              priceRange={priceRange}
              setPriceRange={setPriceRange}
            />
          </div>

          {/* Product Grid */}
          <div className="flex-1">
            <ProductGrid
              category="apparel"
              selectedSubcategory={selectedSubcategory}
              selectedSizes={selectedSizes}
              selectedColors={selectedColors}
              priceRange={priceRange}
              sortBy={sortBy}
            />
          </div>
        </div>
      </div>

      {/* Mobile Filter Modal */}
      {isMobileFilterOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/80"
            onClick={() => setIsMobileFilterOpen(false)}
          ></div>
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-[#0A0A0A] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-black text-white">Filters</h2>
                <button
                  onClick={() => setIsMobileFilterOpen(false)}
                  className="w-10 h-10 flex items-center justify-center text-white hover:bg-white/10 rounded-lg transition-colors"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
              <FilterSidebar
                category="apparel"
                selectedSubcategory={selectedSubcategory}
                setSelectedSubcategory={setSelectedSubcategory}
                selectedSizes={selectedSizes}
                setSelectedSizes={setSelectedSizes}
                selectedColors={selectedColors}
                setSelectedColors={setSelectedColors}
                priceRange={priceRange}
                setPriceRange={setPriceRange}
              />
              <button
                onClick={() => setIsMobileFilterOpen(false)}
                className="w-full mt-8 bg-white text-black py-4 rounded-lg font-bold text-sm tracking-wide hover:scale-105 transition-all duration-300 whitespace-nowrap"
              >
                APPLY FILTERS
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}